/**
 * DurationPicker Component
 * Hours/Minutes split input inside a styled popover with SAVE button.
 * Matches Figma duration modal design.
 */

import { ChevronDown, Clock } from "lucide-react"
import { useCallback, useState } from "react"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib"

interface DurationPickerProps {
  label?: string
  /** Duration in minutes */
  value?: string
  onChange?: (minutes: string) => void
  required?: boolean
  error?: string
  onClearError?: () => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export function DurationPicker({
  label,
  value,
  onChange,
  required,
  error,
  onClearError,
  placeholder = "Set duration",
  disabled,
  className,
}: DurationPickerProps) {
  const [open, setOpen] = useState(false)

  // Parse total minutes into hours/minutes
  const totalMinutes = Number.parseInt(value || "0", 10) || 0
  const [localHours, setLocalHours] = useState(Math.floor(totalMinutes / 60))
  const [localMinutes, setLocalMinutes] = useState(totalMinutes % 60)

  // Sync local state when popover opens
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        const total = Number.parseInt(value || "0", 10) || 0
        setLocalHours(Math.floor(total / 60))
        setLocalMinutes(total % 60)
      }
      setOpen(nextOpen)
    },
    [value],
  )

  const handleSave = useCallback(() => {
    onClearError?.()
    const total = localHours * 60 + localMinutes
    onChange?.(total > 0 ? String(total) : "")
    setOpen(false)
  }, [localHours, localMinutes, onChange, onClearError])

  // Display text
  const displayText =
    totalMinutes > 0
      ? `${Math.floor(totalMinutes / 60)
          .toString()
          .padStart(2, "0")}h ${(totalMinutes % 60).toString().padStart(2, "0")}m`
      : ""

  return (
    <div className={cn("relative space-y-2", className)}>
      {label && (
        <Label
          className={cn(
            "text-xs leading-5 font-normal uppercase mb-[1px]",
            error ? "text-brand-terracotta-bright" : "text-body",
          )}
        >
          {label}
          {required && (
            <span className={error ? "text-brand-terracotta-bright" : "text-body"}>*</span>
          )}
        </Label>
      )}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "w-full pb-2 pt-1 ps-0 rounded-none bg-transparent text-sm font-semibold appearance-none focus:outline-none transition-all duration-200 border-b text-left flex items-center justify-between pr-2",
              error
                ? "border-brand-terracotta-bright text-brand-terracotta-bright"
                : "border-input text-body",
              disabled && "opacity-50 cursor-not-allowed",
            )}
          >
            <span
              className={`flex items-center gap-1 ${displayText ? "text-[var(--color-chat-text)]" : "text-muted-foreground"}`}
            >
              <Clock className="size-4" />
              {displayText || placeholder}
            </span>
            <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={10}
          className="w-auto bg-popover border border-border rounded-[20px] p-[20px]"
        >
          <div className="flex flex-col gap-[20px]">
            <p className="text-[16px] font-semibold text-text-primary">Duration</p>

            <div className="flex gap-[30px]">
              {/* Hours */}
              <div className="flex flex-col gap-[5px]">
                <span className="text-[12px] font-normal text-text-primary opacity-50 uppercase tracking-wide">
                  Hours
                </span>
                <input
                  type="number"
                  min={0}
                  max={99}
                  value={localHours.toString().padStart(2, "0")}
                  onChange={e =>
                    setLocalHours(Math.max(0, Number.parseInt(e.target.value, 10) || 0))
                  }
                  className="w-[80px] bg-transparent text-[24px] font-semibold text-text-primary border-b border-text-primary/20 pb-[5px] focus:outline-none focus:border-text-primary appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
              </div>

              {/* Minutes */}
              <div className="flex flex-col gap-[5px]">
                <span className="text-[12px] font-normal text-text-primary opacity-50 uppercase tracking-wide">
                  Minutes
                </span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={localMinutes.toString().padStart(2, "0")}
                  onChange={e =>
                    setLocalMinutes(
                      Math.min(59, Math.max(0, Number.parseInt(e.target.value, 10) || 0)),
                    )
                  }
                  className="w-[80px] bg-transparent text-[24px] font-semibold text-text-primary border-b border-text-primary/20 pb-[5px] focus:outline-none focus:border-text-primary appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={handleSave}
              className="h-[50px] w-full rounded-full bg-text-primary text-my-rituality-bg text-[14px] font-semibold uppercase"
            >
              Save
            </button>
          </div>
        </PopoverContent>
      </Popover>
      {error && <p className="mt-1 text-xs text-brand-terracotta-bright">{error}</p>}
    </div>
  )
}
